import { DateTime } from "luxon";
import { fromSeconds } from "./date";

/**
 * Benefit period schedules.
 *
 * The database stores only fully exploded period rows (startDate, endDate,
 * amount) — one canonical format with full generality. "Intent" (monthly,
 * calendar-aligned, $15 with a December exception) is never persisted:
 * `explodeSchedule` materializes rows at write time and `inferSchedule`
 * recovers the schedule from rows for editing. Both are exact — a set of
 * rows either is a calendar-aligned monthly schedule or it isn't — so the
 * round trip is lossless.
 *
 * Canonical row encoding: half-open intervals `[startDate, endDate)` at UTC
 * midnight. A January period is [Jan 1, Feb 1): adjacent periods share their
 * boundary instant, tiling is an equality check, and boundary arithmetic
 * never needs days-in-month. Displays render the inclusive last day
 * (endDate minus one day); date pickers convert the other way.
 *
 * Calendar-aligned schedules cover whole calendar units, never partial ones:
 * the coverage bounds snap outward at the start (a card year starting Feb 9
 * still grants the January/Q1 benefit) and floor at the end (the unit
 * containing the fee period end belongs to the next fee period, which claims
 * it as its own first full unit). Consecutive fee periods therefore tile the
 * calendar with no unit granted twice.
 *
 * Legacy inclusive end timestamps (23:59:59 or midnight of the last covered
 * day) are converted by `ceilDay`, which rounds any partial-day end up to
 * the next midnight — an exact conversion for 23:59:59-style values. The
 * one-time migration converts midnight-inclusive stored ends by adding a
 * day, since those are indistinguishable from exclusive boundaries at
 * runtime.
 */

export type BenefitCadence = "monthly" | "quarterly" | "semi_annual" | "annual";

export const MONTHS_PER_CADENCE: Record<BenefitCadence, number> = {
  monthly: 1,
  quarterly: 3,
  semi_annual: 6,
  annual: 12,
};

export type PeriodSeed = {
  startDate: number; // seconds since epoch, UTC midnight of first covered day
  endDate: number; // seconds since epoch, UTC midnight, exclusive
  amount: number; // cents
};

export type BenefitSchedule = {
  cadence: BenefitCadence;
  calendarAligned: boolean;
  /** Base amount in cents (the most common period amount when inferred) */
  amountPerPeriod: number;
  /** Periods whose amount differs from the base, keyed by period startDate */
  exceptions: Array<{ startDate: number; amount: number }>;
};

/** Start of the m-month calendar unit containing `date` (month/quarter/half/year). */
const startOfUnit = (date: DateTime, months: number): DateTime => {
  const unitStartMonth = Math.floor((date.month - 1) / months) * months + 1;
  return date.set({ month: unitStartMonth, day: 1 }).startOf("day");
};

/** Whole-unit coverage span for a calendar-aligned schedule: the start snaps
 * outward to its unit boundary, the end floors to one. A span shorter than a
 * single unit still grants the unit it starts in. */
const calendarSpan = (
  start: DateTime,
  end: DateTime,
  months: number,
): { anchor: DateTime; endBound: DateTime } => {
  const anchor = startOfUnit(start, months);
  const floored = startOfUnit(end, months);
  return {
    anchor,
    endBound: floored > anchor ? floored : anchor.plus({ months }),
  };
};

/** UTC midnight at or before the timestamp — for interval starts. */
export const floorDay = (seconds: number): DateTime =>
  fromSeconds(seconds).startOf("day");

/** UTC midnight at or after the timestamp — for exclusive interval ends. A
 * legacy inclusive end (e.g. Jan 31 23:59:59) rounds up to the correct
 * exclusive boundary (Feb 1); an already-exclusive midnight passes through. */
export const ceilDay = (seconds: number): DateTime => {
  const dt = fromSeconds(seconds);
  const floored = dt.startOf("day");
  return dt.equals(floored) ? floored : floored.plus({ days: 1 });
};

export type ExplodeArgs = {
  cadence: BenefitCadence;
  calendarAligned: boolean;
  amountPerPeriod: number;
  /** Coverage bounds (typically the fee period), endDate exclusive. */
  startDate: number;
  endDate: number;
  exceptions?: Array<{ startDate: number; amount: number }>;
};

/**
 * Materialize a schedule into period rows. Calendar-aligned periods are the
 * whole calendar units of the `calendarSpan`-snapped bounds; anniversary
 * periods stride from the start date with the last row clamped to the end
 * bound. Exceptions override the amount of the row whose startDate matches.
 */
export const explodeSchedule = (args: ExplodeArgs): PeriodSeed[] => {
  const months = MONTHS_PER_CADENCE[args.cadence];
  const start = floorDay(args.startDate);
  const end = ceilDay(args.endDate);
  const { anchor, endBound } = args.calendarAligned
    ? calendarSpan(start, end, months)
    : { anchor: start, endBound: end };

  const periods: PeriodSeed[] = [];
  // Index-based stride (anchor.plus i*months) instead of chained plus, so a
  // Jan 31 anchor yields Mar 31, not the drifted Feb 28 + 1 month = Mar 28
  for (let i = 0; ; i++) {
    const periodStart = anchor.plus({ months: months * i });
    if (periodStart >= endBound) break;
    const periodEnd = anchor.plus({ months: months * (i + 1) });
    // Only an anniversary last row can overrun the bounds — calendar bounds
    // are already unit-aligned
    const clampedEnd = periodEnd > endBound ? endBound : periodEnd;
    const startDate = Math.floor(periodStart.toSeconds());
    const exception = args.exceptions?.find((e) => e.startDate === startDate);
    periods.push({
      startDate,
      endDate: Math.floor(clampedEnd.toSeconds()),
      amount: exception?.amount ?? args.amountPerPeriod,
    });
  }
  return periods;
};

type DayRow = { start: DateTime; end: DateTime; amount: number };

type DayBounds = { startDate: number; endDate: number };

/** Rows are exactly the whole calendar units of the unit-snapped bounds —
 * the same span explodeSchedule would produce. */
const matchesCalendar = (
  rows: DayRow[],
  months: number,
  bounds: DayBounds,
): boolean => {
  const { anchor, endBound } = calendarSpan(
    floorDay(bounds.startDate),
    ceilDay(bounds.endDate),
    months,
  );
  if (!rows[rows.length - 1].end.equals(endBound)) return false;
  for (let i = 0; i < rows.length; i++) {
    if (!rows[i].start.equals(anchor.plus({ months: months * i }))) return false;
    if (!rows[i].end.equals(anchor.plus({ months: months * (i + 1) }))) return false;
  }
  return true;
};

/** Rows stride m months from the bounds start to the bounds end; the last
 * row may be clamped short of a full stride. */
const matchesAnniversary = (
  rows: DayRow[],
  months: number,
  bounds: DayBounds,
): boolean => {
  const anchor = floorDay(bounds.startDate);
  if (!rows[rows.length - 1].end.equals(ceilDay(bounds.endDate))) return false;
  for (let i = 0; i < rows.length; i++) {
    const { start, end } = rows[i];
    if (!start.equals(anchor.plus({ months: months * i }))) return false;
    const strideEnd = anchor.plus({ months: months * (i + 1) });
    if (i < rows.length - 1) {
      if (!end.equals(strideEnd)) return false;
    } else {
      if (end > strideEnd || end <= start) return false;
    }
  }
  return true;
};

/**
 * Recover the schedule from stored rows, or null when the rows are genuinely
 * irregular (overlapping, gapped, or not on any cadence stride). Amounts do
 * not affect matching: the most common amount becomes the base and the rest
 * become exceptions.
 *
 * `bounds` (the fee period dates) must be covered exactly — for calendar
 * schedules, after unit-snapping — because re-exploding an inferred schedule
 * spans the full bounds. A partial list — say only Jan+Feb rows inside an
 * annual fee period — must stay custom, or re-saving would silently add the
 * missing months.
 */
export const inferSchedule = (
  periods: ReadonlyArray<{ startDate: number; endDate: number; amount: number }>,
  bounds: { startDate: number; endDate: number },
): BenefitSchedule | null => {
  if (periods.length === 0) return null;
  const rows: DayRow[] = [...periods]
    .sort((a, b) => a.startDate - b.startDate)
    .map((p) => ({
      start: floorDay(p.startDate),
      end: ceilDay(p.endDate),
      amount: p.amount,
    }));

  for (const row of rows) {
    if (row.end <= row.start) return null;
  }
  // Contiguous, non-overlapping coverage: adjacent rows share the boundary
  for (let i = 1; i < rows.length; i++) {
    if (!rows[i].start.equals(rows[i - 1].end)) return null;
  }

  // Each matcher pins the rows to the bounds (first start, last end, exact
  // strides), so a lone short row can only match a cadence whose re-explosion
  // reproduces it exactly — no expansion on re-save.
  let matched: { cadence: BenefitCadence; calendarAligned: boolean } | null = null;
  const cadences: BenefitCadence[] = ["monthly", "quarterly", "semi_annual", "annual"];
  for (const cadence of cadences) {
    const months = MONTHS_PER_CADENCE[cadence];
    if (matchesCalendar(rows, months, bounds)) {
      matched = { cadence, calendarAligned: true };
      break;
    }
    if (matchesAnniversary(rows, months, bounds)) {
      matched = { cadence, calendarAligned: false };
      break;
    }
  }
  if (!matched) return null;

  // Base amount = most common (earliest wins ties); the rest are exceptions
  const counts = new Map<number, number>();
  for (const row of rows) {
    counts.set(row.amount, (counts.get(row.amount) ?? 0) + 1);
  }
  let base = rows[0].amount;
  let bestCount = 0;
  for (const row of rows) {
    const count = counts.get(row.amount) ?? 0;
    if (count > bestCount) {
      base = row.amount;
      bestCount = count;
    }
  }
  const exceptions = rows
    .filter((row) => row.amount !== base)
    .map((row) => ({
      startDate: Math.floor(row.start.toSeconds()),
      amount: row.amount,
    }));

  return {
    cadence: matched.cadence,
    calendarAligned: matched.calendarAligned,
    amountPerPeriod: base,
    exceptions,
  };
};

/**
 * Re-explode rows that read as a schedule over new bounds — used when
 * copying benefits to the next fee period and when a fee period's dates
 * change. Amount exceptions carry over by structural position when the row
 * counts match, otherwise by nth-occurrence-of-month for calendar schedules
 * (plain month matching would collide across years or clamped duplicates).
 * Returns null when the rows are custom (no inferable schedule).
 */
export const regenerateSchedule = (
  sourceRows: ReadonlyArray<{ startDate: number; endDate: number; amount: number }>,
  sourceBounds: { startDate: number; endDate: number },
  targetBounds: { startDate: number; endDate: number },
): PeriodSeed[] | null => {
  const schedule = inferSchedule(sourceRows, sourceBounds);
  if (!schedule) return null;

  const rows = explodeSchedule({
    cadence: schedule.cadence,
    calendarAligned: schedule.calendarAligned,
    amountPerPeriod: schedule.amountPerPeriod,
    startDate: targetBounds.startDate,
    endDate: targetBounds.endDate,
  });
  const sorted = [...sourceRows].sort((a, b) => a.startDate - b.startDate);

  // Calendar rows start on unit boundaries, but legacy callers may still
  // hold clamped rows (a Q1 row starting Mar 15 is still Q1), so matching
  // keys on the containing unit's start month instead of the raw start month
  const months = MONTHS_PER_CADENCE[schedule.cadence];
  const unitMonth = (startDate: number): number =>
    startOfUnit(floorDay(startDate), months).month;

  // nth occurrence of the row's calendar unit within a row list — the
  // ordinal disambiguates repeated units across multi-year spans
  const occurrenceOfUnit = (
    list: ReadonlyArray<{ startDate: number }>,
    startDate: number,
  ): number =>
    list.filter(
      (r) =>
        r.startDate < startDate && unitMonth(r.startDate) === unitMonth(startDate),
    ).length;

  // Exception startDates come from inference (day-floored); compare source
  // rows at the same granularity so legacy non-midnight starts still match
  const sourceIndexOf = (startDate: number): number =>
    sorted.findIndex((p) => floorDay(p.startDate).equals(floorDay(startDate)));

  for (const exception of schedule.exceptions) {
    let target: PeriodSeed | undefined;
    if (schedule.calendarAligned && rows.length !== sorted.length) {
      const month = unitMonth(exception.startDate);
      const occurrence = occurrenceOfUnit(sorted, exception.startDate);
      target = rows.filter((r) => unitMonth(r.startDate) === month)[occurrence];
    } else {
      // Structural position from the anchor — holds for equal-count copies
      // and for anniversary schedules whose anchor moved
      target = rows[sourceIndexOf(exception.startDate)];
    }
    if (target) {
      target.amount = exception.amount;
    }
  }
  return rows;
};
